import React, { useState, useEffect } from 'react';
import { apiClient, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, 
  Plus, 
  TrendingUp,
  TrendingDown,
  Heart,
  Calendar,
  Receipt,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import NewIncomeForm from '../components/forms/NewIncomeForm';
import NewExpenseForm from '../components/forms/NewExpenseForm';

const FinancePage = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const { isDoctor, isCashier } = useAuth();

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      const [recordsRes, summaryRes] = await Promise.all([
        apiClient.get('/financial-records'),
        apiClient.get(`/financial-summary?period=${period}`)
      ]);
      setRecords(recordsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const incomeRecords = records.filter(r => r.record_type === 'income');
  const expenseRecords = records.filter(r => r.record_type === 'expense');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
            <DollarSign className="w-6 h-6 text-primary" strokeWidth={1.5} />
            Finance
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track income and expenses
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowIncomeForm(true)}
            className="rounded-full"
            data-testid="log-income-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Log Income
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowExpenseForm(true)}
            className="rounded-full"
            data-testid="log-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Log Expense
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex items-center gap-4">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="period-select">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm text-muted-foreground">Income</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary?.total_income)}</p>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <span className="text-sm text-muted-foreground">Expenses</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary?.total_expenses)}</p>
          </CardContent>
        </Card>

        <Card className={`${(summary?.net || 0) >= 0 ? 'border-primary bg-primary/5' : 'border-red-300 bg-red-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm text-muted-foreground">Net</span>
            </div>
            <p className={`text-2xl font-bold ${(summary?.net || 0) >= 0 ? 'text-primary' : 'text-red-600'}`}>
              {formatCurrency(summary?.net)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Care Given</span>
            </div>
            <p className="text-2xl font-bold text-primary">{formatCurrency(summary?.care_given)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Waived: {formatCurrency(summary?.waived_amount)} • Discounts: {formatCurrency(summary?.discount_amount)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Records Tabs */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All ({records.length})</TabsTrigger>
          <TabsTrigger value="income">Income ({incomeRecords.length})</TabsTrigger>
          <TabsTrigger value="expenses">Expenses ({expenseRecords.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-3 mt-4">
          {records.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No records yet</p>
              </CardContent>
            </Card>
          ) : (
            records.slice(0, 50).map((record, index) => (
              <RecordCard key={record.record_id} record={record} index={index} formatCurrency={formatCurrency} formatDate={formatDate} />
            ))
          )}
        </TabsContent>

        <TabsContent value="income" className="space-y-3 mt-4">
          {incomeRecords.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No income records</p>
              </CardContent>
            </Card>
          ) : (
            incomeRecords.slice(0, 50).map((record, index) => (
              <RecordCard key={record.record_id} record={record} index={index} formatCurrency={formatCurrency} formatDate={formatDate} />
            ))
          )}
        </TabsContent>

        <TabsContent value="expenses" className="space-y-3 mt-4">
          {expenseRecords.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No expense records</p>
              </CardContent>
            </Card>
          ) : (
            expenseRecords.slice(0, 50).map((record, index) => (
              <RecordCard key={record.record_id} record={record} index={index} formatCurrency={formatCurrency} formatDate={formatDate} />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Income Dialog */}
      <Dialog open={showIncomeForm} onOpenChange={setShowIncomeForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Income / Payment</DialogTitle>
          </DialogHeader>
          <NewIncomeForm 
            onSuccess={() => {
              setShowIncomeForm(false);
              fetchData();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={showExpenseForm} onOpenChange={setShowExpenseForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Expense</DialogTitle>
          </DialogHeader>
          <NewExpenseForm 
            onSuccess={() => {
              setShowExpenseForm(false);
              fetchData();
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RecordCard = ({ record, index, formatCurrency, formatDate }) => {
  const isIncome = record.record_type === 'income';
  
  return (
    <Card 
      className={`animate-fade-in border-l-4 ${isIncome ? 'border-l-green-500' : 'border-l-red-500'}`}
      style={{ animationDelay: `${index * 0.03}s` }}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isIncome ? 'bg-green-100' : 'bg-red-100'}`}>
              {isIncome ? (
                <Receipt className="w-5 h-5 text-green-600" />
              ) : (
                <FileText className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium capitalize">
                  {isIncome ? record.category : record.item_name}
                </span>
                <Badge variant="outline" className="text-xs">
                  {isIncome ? record.payment_method : record.category}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                {formatDate(record.created_at)}
                {record.patient_name && ` • ${record.patient_name}`}
              </div>
              {(record.waived_amount > 0 || record.discount_amount > 0) && (
                <div className="flex gap-2 mt-1">
                  {record.waived_amount > 0 && (
                    <Badge className="bg-primary/10 text-primary text-xs">
                      Waived: {formatCurrency(record.waived_amount)}
                    </Badge>
                  )}
                  {record.discount_amount > 0 && (
                    <Badge className="bg-accent/10 text-accent text-xs">
                      Discount: {formatCurrency(record.discount_amount)}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <p className={`text-lg font-bold ${isIncome ? 'text-green-600' : 'text-red-600'}`}>
            {isIncome ? '+' : '-'}{formatCurrency(record.amount)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FinancePage;
